import { definitions } from './tRetailTypes';
import {
	BookingMessage,
	GlobalSettingsFeatureFlags,
	PreferredPartnerToolsAndResourcesSubmitSiteVisitRequestContent,
	SearchPagesResult,
	SimplePropertyResponse,
	TermsAndConditionsResponse,
} from '@fshr/microservices/compiled/content.v1';
import type { ProfileV1 } from '@fshr/microservices/profile';
import type { BookingV1 } from '@fshr/microservices/booking';

export type WhatsIncludedTitleDictKey = {
	key: string;
	html: boolean;
};

export type Amount = {
	withDecimal: string;
	withoutDecimal: number;
};

export type Price = {
	amount: Amount;
	currency: string;
};

export type BookDirectPhone = {
	number: string;
};

export type OffersItem = {
	owsCode: string;
	orsCode: string;
	title: string;
	shortDescription: string;
	longDescription: string;
	whatsIncludedTitleDictKey: WhatsIncludedTitleDictKey;
	cancellationPolicy: string;
	cancellationDeadline: number;
	cancellationDeadlineHours: number;
	cancellationBy: string;
	price: Price;
	feesDisclaimer: string;
	inclusionsTitle: string;
	inclusions: Array<string>;
	disclaimer: string;
	image: Array<string>;
	bookDirectPhone: BookDirectPhone;
	priceId: number;
	resultId: string;
};

export type BedCustTypesItem = {
	offers: Array<OffersItem>;
	owsCode: string;
	orsCode: string;
	accessible: boolean;
	accessibleOnly: boolean;
	description: string;
	remainingUnits: number;
};

export type ServicesAndAmenitiesItem = {
	title: string;
	icon: string;
	amenity: Array<string>;
};

export type ImagesItem = {
	path: string;
};

export type LowestRate = {
	amount: Amount;
	currency: string;
};

export type BookDirectData = {
	bookDirectTitle: string;
	bookDirectPhone: string;
	propertyPhone: string;
};

export type RoomCustTypesItem = {
	bedCustTypes: Array<BedCustTypesItem>;
	name: string;
	description: string;
	view: Array<string>;
	size: string;
	occupancy: Array<string>;
	extraBed: string;
	location: string;
	bathroom: Array<string>;
	uniqueFeatures: Array<string>;
	servicesAndAmenities: Array<ServicesAndAmenitiesItem>;
	images: Array<ImagesItem>;
	lowestRate: LowestRate;
	bedOptions: Array<string>;
	bookableOnline: boolean;
	feesDisclaimer: string;
	bookDirectData: BookDirectData;
};

export type RoomCategoriesItem = {
	Name: string;
	roomCustTypes: Array<RoomCustTypesItem>;
};

export type SearchResultsItem = {
	roomIndex: number;
	roomCategories: Array<RoomCategoriesItem>;
};

export type BookingMessagesItem = {
	title: string;
	body: string;
	type: string;
};

export type PropertyImage = {
	path: string;
};

export type WebChatItem = {
	enabled: boolean;
	id: string;
	integrationId: string;
	contentSection: string;
	propertyName: string;
	propertyCode: string;
	propertyImage: PropertyImage;
};

export type ExchangeRatesItem = {
	code: string;
	title: string;
	rate: string;
};

export type TextDictKey = {
	key: string;
	html: boolean;
};

export type TollFreeNumbers = {
	url: string;
	textDictKey: TextDictKey;
};

export type ContactUs = {
	url: string;
	textDictKey: TextDictKey;
};

export type HotelOptionsItem = {
	supplierId: number;
	searchResults: Array<SearchResultsItem>;
	propertyName: string;
	bookingMessages: Array<BookingMessagesItem>;
	disclaimer: string;
	webChat: Array<WebChatItem>;
	exchangeRates: Array<ExchangeRatesItem>;
	propertySortBy: string;
	tollFreeNumbers: TollFreeNumbers;
	contactUs: ContactUs;
};

export type GetSearchResults = definitions['hotelCustSearchResults'] | definitions['errorModel'];

export type OJError = {
	errorCode: string;
};

export type CreateSearchResults = {
	hotelSearchResultSets?: {
		resultSetId?: string;
	}[];
	supplierErrors?: OJError[];
	apiErrors?: OJError[];
	warnings?: OJError[];
};

export type CreateSearchInput = {
	locale: string;
	//this can all be null, which will be the default
	//it won't change to true or false until the user
	//changes the falue on the frontend
	allInPricing?: boolean;
	checkinDate: string;
	hotelCode: string;
	checkoutDate: string;
	promoCode?: string;
	currencyCode?: string;
	sortBy?: string;
	accessibleRoomsOnly?: boolean;
	rooms?: Room[];
	roomOfferFilter?: string;
	showPrivateRetreats?: boolean;
	bedTypeFilterCodes?: string;
	roomSelections?: string;
};

export type GetSearchInput = {
	locale: string;
	resultSetId: string;
	currencyCode?: string;
	sortBy?: string;
	accessibleRoomsOnly: boolean;
	roomOfferFilter?: string;
	promoCode?: string;
	showPrivateRetreats?: boolean;
	bedTypeFilterCodes?: string;
	roomSelections?: string;
};

export type GetBookingInput = {
	bookingId: string;
	signal?: AbortSignal;
	locale: string;
};

export type AddToCartInput = {
	locale: string;
	resultSetId: string;
	resultId: string;
	bookNowClicked?: boolean;
};
export type CreateBookingInput = {
	locale: string;
};

export type AddHotelProductInput = {
	locale: string;
	bookingId: string;
	resultSetId: string;
	resultId: string;
	ppMode?: boolean;
};

export type Room = {
	adults: number;
	children: number;
};
export type PlanStoreSearch = Partial<
	Omit<CreateSearchInput, 'checkinDate' | 'checkoutDate'> & {
		checkinDate: Date;
		checkoutDate: Date;
		flexDatesSelected?: number;
		completedSearch?: boolean;
		searchIsDirty?: boolean;
		roomCode?: string;
		offerCode?: string;
		showPrivateRetreats?: boolean;
	}
>;
export type TimeFilter = {
	startTime: string;
	endTime: string;
	label: string;
};
export type SubCategoryFilter = { name: string; id: string };

export type Package = {
	roomName?: string;
	offerOwsCode?: string;
	owsCode?: string;
	offerTitle?: string;
	priceId: number;
	resultId: string;
};

export type AvailabilityAnalyticsData = {
	event_name: string;
	reco_property_name: string[];
	reco_property_code: string[];
	avail_check_template: string[];
	booking_arrival_date: string[];
	booking_departure_date: string[];
	booking_availability: string[];
	booking_number_rooms: number;
	booking_discount_code: string;
	flex_count: number;
	room_type: string;
	room_nights_array: string[];
	room_adults_array: string[];
	room_children_array: string[];
	room_property_array: string[];
	room_finding_method_array: string[];
	room_code_array: string[];
	reco_room_code_array: string[];
	room_name_array: string[];
	room_rate_array: string[];
	room_type_array: string[];
	room_package_code_array: string[];
	room_package_name_array: string[];
	room_package_ows_code_array: string[];
	reco_room_package_code_array: string[];
	property_datalake_reco_id: string[];
	aem_url: string;
	serial_id: string;
	property_reco_flag: string[];
	multiroom_booking_step?: string;
	avail_result_selection?: string;
	user_type?: string;
	user_id?: string;
	golden_id?: string;
	aa_account?: string;
	pp_id?: string;
	pp_agency?: string;
	property_code?: string;
	property_name?: string;
	property_region?: string;
	property_subregion?: string;
};

export type SearchResultsReturn = {
	numberOfResults: number;
	results: {
		properties: SearchPagesPropertyResult[];
		other: SearchPagesResult[];
	};
};

export type SearchPagesPropertyResult = SearchPagesResult & { image?: string; location?: string };

export type FeaturedContentItem = {
	image?: string;
	title?: string;
	description?: string;
	url?: string;
};

export type FeaturedContentResponse = { result: { featuredContent: FeaturedContentItem[] } };

export type NavigationLink = {
	text: string;
	link: string;
	openInNewTab: boolean;
	geoIpRestrictions: string[];
};

export type NavigationLinkCategory = {
	label: string;
	links: NavigationLink[];
};

export type NavigationFooterLinks = {
	linkCategories: NavigationLinkCategory[];
	legalLinks: NavigationLink[];
	socialLinks: NavigationLink[];
	contactEmail?: string;
	copyright?: string;
};

export type PreferredPartnerLabeledCta = {
	label?: string;
	cta: NavigationLink;
};

export type PreferredPartnerFooterContact = {
	heading: string;
	generalQuestionsLabel: string;
	email?: string;
	allContactsCta?: PreferredPartnerLabeledCta;
	illume?: PreferredPartnerLabeledCta;
};

export type PreferredPartnerFooter = {
	contact: PreferredPartnerFooterContact;
	linkCategories: NavigationLinkCategory[];
	legalLinks: NavigationLink[];
	socialLinks: NavigationLink[];
};

export type NavigationAndFooterLinks = {
	topBarLinks: NavigationLink[];
	header: NavigationLink[];
	footer: PreferredPartnerFooter;
};

export type DailyRate = {
	date?: string;
	totalDailyRate: TotalInfo;
	resortFees: TaxFeeItem[];
};

export type TotalInfo = {
	amount: string;
	currency: string;
};

export type TaxFeeItem = {
	amount: string;
	currency: string;
	description: string;
};

export type BookingData = {
	roomType: string;
	confirmationNumber: string;
	stayDates?: string;
	offer: string;
	averageNigthlyPrice?: TotalInfo;
	totalRoomRate: TotalInfo;
	taxes: TaxFeeItem[];
	resortFees: TaxFeeItem[];
	taxesAndFeesTotal?: TotalInfo;
	estimatedTotal: TotalInfo;
	priceViewable?: boolean;
	dailyRates: DailyRate[];
	feesDisclaimerCheckout?: string;
	bookingMessages: BookingMessage[];
	estimatedTotalDisclaimer: string;
};

export type PPNavigationAndFooterLinksResponse = {
	data: NavigationAndFooterLinks;
	error?: string;
};

export type ProfileBookingProperty = {
	id?: string;
	name: string;
	owsCode?: string;
	orsCode?: string;
};

export type ProfileBookingPrimaryGuest = {
	lastName: string;
};

export type ProfileBooking = {
	id?: string;
	bookingId?: string;
	confirmationNumber: string;
	primaryGuest: ProfileBookingPrimaryGuest;
	checkInDate: string;
	checkOutDate: string;
	property: ProfileBookingProperty;
};

export type PPOverviewData = {
	bookings: ProfileBooking[];
};

export type PPOverviewResponse = {
	data: PPOverviewData;
	error?: string;
};

export type PPManageAccountData = {
	centralReservationsOfficeEmail?: string;
	centralReservationsOfficeFax?: string;
};

export type PreferredPartnerProfile = ProfileV1.PpProfileResponse;

export type PreferredPartnerGlobalStateResponse = {
	preferredCurrency: string;
	featureFlags: GlobalSettingsFeatureFlags;
	globalEmail?: string;
	isAuthenticated: boolean;
	profile?: PreferredPartnerProfile;
	activeBookingId?: string;
};

export type PreferredPartnerBookingReturn = {
	bookingData: BookingV1.FindReservationResponse;
	propertyData: SimplePropertyResponse;
	termsAndConditionsData?: TermsAndConditionsResponse;
};

export type PPBookingData = BookingData & {
	firstName: string;
	lastName: string;
	isPreferredPartner?: boolean;
	guestPrimaryEmail?: string;
};

export type PPReservationDetailsData = {
	bookingId: string;
	booking: PPBookingData;
	propertyShortName?: string;
	bookingCancellable?: boolean;
};

export type PPUpcomingBookingsPage = {
	title?: string;
	description?: string;
	upcomingBookings: PPUpcomingBooking[];
};

export type PPUpcomingBooking = BookingV1.PpUpcomingBooking & {
	propertyShortName: string;
};

export type SubmitSiteVisitRequestPropertyContent = {
	id?: string;
	owsCode?: string;
	shortName?: string;
	sortingName: string;
};

export type SubmitSiteVisitRequestContentResponse = Omit<
	PreferredPartnerToolsAndResourcesSubmitSiteVisitRequestContent,
	'bcc'
> & {
	properties: SubmitSiteVisitRequestPropertyContent[];
};

export type PPCheckCommissionStatusContent = {
	title?: string;
	description?: string;
	confirmationMessage?: string;
	upcomingBookings: PPUpcomingBooking[];
	properties: PPCheckCommissionStatusProperty[];
};

export type PPCheckCommissionStatusProperty = {
	id?: string;
	owsCode?: string;
	orsCode?: string;
	shortName?: string;
	sortingName: string;
};

/** Calendar day availability — API returns more than the generated spec includes. */
export type CalendarAvailability = {
	date: string;
	status: string;
	nightlyRates?: number[];
	currencyCode?: string;
};

export function getCalendarCurrencyCode(calendarResult?: definitions['calendarResult']): string {
	const availability = calendarResult?.calendar?.[0]?.availability?.[0] as
		| CalendarAvailability
		| undefined;
	return availability?.currencyCode ?? '';
}
